import EditorMenuControls from './EditorMenuControls';
import { Box } from '@mui/material';
import { LinkBubbleMenu, RichTextEditor, TableBubbleMenu, insertImages } from 'mui-tiptap';
import { useExtensions } from './useExtensions';
import './TextEditor.css';
import useUpload from '../../hooks/upload/useUpload';
import { useCallback, useRef, useEffect } from 'react';

// helper: extract image files from FileList (drop or paste)
const fileListToImageFiles = (fileList) => {
  return Array.from(fileList).filter((file) => {
    const mimeType = (file.type || '').toLowerCase();
    return mimeType.startsWith('image/');
  });
};

const TextEditor = ({
  content = '',
  markdown = '',
  isEditable = true,
  showMenuBar = true,
  placeholder = 'Write something...',
  editorVariant = 'outlined',
  error,
  setContent = () => {},
  setMarkdown = () => {},
  typeUploadFolder = 'public'
}) => {
  const rteRef = useRef(null);
  const extensions = useExtensions({
    placeholder
  });

  // useUpload ahora devuelve uploadFile, srcFile y deleteFiles
  const { uploadFile, srcFile, deleteFiles } = useUpload();

  // Keep track of images currently in the document to detect removals
  const prevImageSrcsRef = useRef(new Set());
  const deletedFilesRef = useRef(new Set()); // avoid duplicate delete calls

  // helper: ask backend for signed S3 URL and upload file, return public fileUrl
  const uploadImageToS3 = useCallback(
    async (file) => {
      try {
        const tempId = `${Date.now()}-${Math.floor(Math.random() * 10000)}`;
        const fileData = {
          id: tempId,
          name: file.name,
          type: file.type,
          size: file.size,
          file
        };
        // uploadFile expects an array of { id, file } and returns { error, data: [{ id, name, link }] }
        const { error, data } = await uploadFile([fileData], typeUploadFolder);
        if (error || !data || !data[0]) {
          console.error('uploadFile failed', error);
          return null;
        }

        const publicLink = srcFile(data[0].link, typeUploadFolder);
        return publicLink;
      } catch (err) {
        console.error('Image upload failed', err);
        return null;
      }
    },
    [uploadFile, srcFile, typeUploadFolder]
  );

  const handleNewImageFiles = useCallback(
    (files, insertPosition) => {
      if (!rteRef.current?.editor) return;
      const editor = rteRef.current.editor;

      // Insert temporary blob urls for immediate UX
      const attributesForImageFiles = files.map((file) => ({
        src: URL.createObjectURL(file),
        alt: file.name,
        id: file._id || file.id || `${Date.now()}-${Math.floor(Math.random() * 10000)}`,
        __localBlob: true // marker for replacement later
      }));

      insertImages({
        images: attributesForImageFiles,
        editor,
        insertPosition
      });

      // Upload each file and replace img src in doc when done
      files.forEach(async (file, idx) => {
        try {
          const localBlobUrl = attributesForImageFiles[idx].src;
          const uploadedUrl = await uploadImageToS3(file);

          if (!uploadedUrl) {
            // upload failed: you may want to show an error indicator or remove the image
            return;
          }

          const { state } = editor;
          const tr = state.tr;
          let updated = false;

          state.doc.descendants((node, pos) => {
            if (!node) return;
            if (node.type && node.type.name === 'image' && node.attrs && node.attrs.src === localBlobUrl) {
              const newAttrs = { ...node.attrs, src: uploadedUrl };
              tr.setNodeMarkup(pos, undefined, newAttrs);
              updated = true;
            }
          });

          if (updated) {
            editor.view.dispatch(tr);
          } else {
            // As fallback, insert uploaded image at current selection end
            editor.chain().focus().setImage({ src: uploadedUrl, alt: file.name }).run();
          }

          try {
            URL.revokeObjectURL(localBlobUrl);
          } catch (e) {
            /* ignore */
          }
        } catch (err) {
          console.error('Error uploading/replacing image', err);
        }
      });
    },
    [uploadImageToS3]
  );

  // handle drop event
  const handleDrop = useCallback(
    (view, event, _slice, _moved) => {
      if (!(event instanceof DragEvent) || !event.dataTransfer) return false;

      const files = event.dataTransfer.files;
      const imageFiles = fileListToImageFiles(files);
      if (imageFiles.length > 0) {
        const insertPosition = view.posAtCoords({
          left: event.clientX,
          top: event.clientY
        })?.pos;
        handleNewImageFiles(imageFiles, insertPosition);
        event.preventDefault();
        return true;
      }
      return false;
    },
    [handleNewImageFiles]
  );

  // handle paste event (files or clipboard items)
  const handlePaste = useCallback(
    (view, event, _slice) => {
      if (!event.clipboardData) return false;

      // Prefer files (modern browsers)
      let imageFiles = fileListToImageFiles(event.clipboardData.files || []);
      // If no files, try clipboard items (some browsers put images in items)
      if (imageFiles.length === 0 && event.clipboardData.items) {
        const items = Array.from(event.clipboardData.items);
        const extracted = items
          .map((it) => {
            if (it.kind === 'file' && (it.type || '').startsWith('image/')) return it.getAsFile();
            return null;
          })
          .filter(Boolean);
        if (extracted.length) imageFiles = extracted;
      }

      if (imageFiles.length > 0) {
        // insert at current selection
        handleNewImageFiles(imageFiles);
        event.preventDefault();
        return true;
      }

      return false;
    },
    [handleNewImageFiles]
  );

  useEffect(() => {
    if (!rteRef.current?.editor) return;

    const editor = rteRef.current.editor;

    const collectImageSrcs = (doc) => {
      const set = new Set();
      doc.descendants((node) => {
        if (node?.type?.name === 'image' && node.attrs?.src) {
          set.add(node.attrs.src);
        }
      });
      return set;
    };

    const handleUpdate = async ({ editor }) => {
      const markdown = editor.storage.markdown.getMarkdown();

      setMarkdown(markdown);
      setContent(editor.getHTML());

      // detect removed images: compare previous srcs with current
      try {
        const currentSrcs = collectImageSrcs(editor.state.doc);
        const prev = prevImageSrcsRef.current || new Set();
        const removed = Array.from(prev).filter((s) => !currentSrcs.has(s));
        // Filter out local blobs/data URLs (we don't delete those on server)
        const removableSrcs = removed.filter(
          (src) => src && !src.startsWith('blob:') && !src.startsWith('data:')
        );

        // Exclude already deleted
        const toDelete = removableSrcs.filter((src) => !deletedFilesRef.current.has(src));

        if (toDelete.length > 0 && typeof deleteFiles === 'function') {
          // prepare payload expected by backend: use fileUrl so backend can resolve key
          const payload = toDelete.map(
            (src) => src.split('/').slice(-1)[0] // extract filename from URL
          );
          try {
            const res = await deleteFiles(payload, typeUploadFolder);
            // If deleteFiles returns error/data follow hook contract; mark as deleted when success
            if (!res?.error) {
              toDelete.forEach((src) => deletedFilesRef.current.add(src));
            } else {
              console.error('deleteFiles returned error', res.error);
            }
          } catch (err) {
            console.error('Failed to delete removed images from storage', err, toDelete);
          }
        }

        // update prev set
        prevImageSrcsRef.current = currentSrcs;
      } catch (err) {
        console.error('Error detecting removed images', err);
      }
    };

    // initialize prevImageSrcsRef with current doc images
    prevImageSrcsRef.current = collectImageSrcs(editor.state.doc);

    editor.on('update', handleUpdate);

    return () => {
      editor.off('update', handleUpdate);
    };
  }, [setContent, setMarkdown, deleteFiles, typeUploadFolder]);

  return (
    <>
      <Box
        sx={{
          // An example of how editor styles can be overridden.
          '& .ProseMirror': {
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              scrollMarginTop: showMenuBar ? 50 : 0
            }
          }
        }}
      >
        <RichTextEditor
          ref={rteRef}
          extensions={extensions}
          content={content ?? ``}
          editable={isEditable}
          editorProps={{
            handleDrop: handleDrop,
            handlePaste: handlePaste
          }}
          renderControls={() => <EditorMenuControls uploadImageToS3={handleNewImageFiles} />}
          RichTextFieldProps={{
            variant: editorVariant, // 'standard' | 'outlined'
            MenuBarProps: {
              hide: !showMenuBar
            },
            footer: <></> //Footer content
          }}
        >
          {() => (
            <>
              <LinkBubbleMenu />
              <TableBubbleMenu />
            </>
          )}
        </RichTextEditor>
      </Box>
      {error && <Box className='text-editor--error'>{error}</Box>}
    </>
  );
};

export default TextEditor;
